import { useEffect, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  TestWhatsAppResponse,
  WhatsAppConnectionResponse,
  WhatsAppLinkState,
  WhatsAppSettingsResponse,
} from "@mubin/contracts";
import { api, errorText, send } from "../lib/api";
import { Badge, ErrorNote, Field, useSubmit } from "./ui";
import { PhoneField } from "./phone-field";

/**
 * The organization's WhatsApp gateway (ADR 0019), like Deem's settings: base URL, instance and token. Sign-in
 * codes go through it. The token is write-only: the portal never receives it back.
 */
export function WhatsAppSettingsCard({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const key = ["whatsapp-settings", organizationId];
  const settings = useQuery({
    queryKey: key,
    queryFn: () => api<WhatsAppSettingsResponse>(`/v1/whatsapp-settings?organizationId=${organizationId}`),
  });
  const save = useSubmit();
  const test = useSubmit();
  const [form, setForm] = useState({ baseUrl: "", instanceId: "", token: "", active: true });
  const [testPhone, setTestPhone] = useState("");
  const [result, setResult] = useState<TestWhatsAppResponse | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data)
      setForm({
        baseUrl: settings.data.baseUrl ?? "https://api.ultramsg.com",
        instanceId: settings.data.instanceId ?? "",
        token: "",
        active: settings.data.hasToken ? settings.data.active : true,
      });
  }, [settings.data]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setSaved(false);
    void save.run(async () => {
      await send("PUT", "/v1/whatsapp-settings", {
        organizationId,
        baseUrl: form.baseUrl.trim(),
        instanceId: form.instanceId.trim(),
        token: form.token.trim() || undefined,
        active: form.active,
      });
      await qc.invalidateQueries({ queryKey: key });
      setForm((f) => ({ ...f, token: "" }));
      setSaved(true);
    }, errorText);
  }

  function sendTest() {
    setResult(null);
    void test.run(async () => {
      setResult(
        await send<TestWhatsAppResponse>("POST", "/v1/whatsapp-settings/test", {
          organizationId,
          phone: testPhone.trim(),
        }),
      );
      await qc.invalidateQueries({ queryKey: key });
    }, errorText);
  }

  const s = settings.data;
  const valid = form.baseUrl.trim() && form.instanceId.trim() && (s?.hasToken || form.token.trim());

  return (
    <section className="card">
      <div className="card-h">
        <h2>ربط الواتساب</h2>
        {s?.hasToken &&
          (s.lastTestedOk === null ? (
            <Badge tone="neutral">لم يُختبر</Badge>
          ) : s.lastTestedOk ? (
            <Badge tone="success">يعمل</Badge>
          ) : (
            <Badge tone="danger">فشل آخر اختبار</Badge>
          ))}
      </div>
      <form className="section form" onSubmit={submit}>
        <p className="muted small">
          رموز الدخول إلى التطبيق والبوابة تصل للمستخدمين عبر الواتساب من خلال هذه الخدمة. أدخل بيانات حساب
          خدمة الواتساب (مثل UltraMsg أو خادمكم بنفس الواجهة). الرمز (Token) يُحفظ ولا يُعرض مرة أخرى.
        </p>
        <ErrorNote error={save.error} />
        <div className="form-row">
          <Field id="wa-base" label="رابط الخدمة (Base URL)">
            <input
              id="wa-base"
              dir="ltr"
              value={form.baseUrl}
              onChange={(e) => setForm({ ...form, baseUrl: e.target.value })}
              placeholder="https://api.ultramsg.com"
              required
            />
          </Field>
          <Field id="wa-instance" label="معرّف الحساب (Instance ID)">
            <input
              id="wa-instance"
              dir="ltr"
              value={form.instanceId}
              onChange={(e) => setForm({ ...form, instanceId: e.target.value })}
              placeholder="instance12345"
              required
            />
          </Field>
        </div>
        <Field
          id="wa-token"
          label="الرمز (Token)"
          hint={s?.hasToken ? "محفوظ. اتركه فارغًا للإبقاء عليه، أو أدخل رمزًا جديدًا" : undefined}
        >
          <input
            id="wa-token"
            type="password"
            dir="ltr"
            autoComplete="off"
            value={form.token}
            onChange={(e) => setForm({ ...form, token: e.target.value })}
            placeholder={s?.hasToken ? "••••••••" : ""}
          />
        </Field>
        <label className="check">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          مفعّل (عند التعطيل لا تُرسل رموز الدخول، ويبقى الدخول بكلمة المرور)
        </label>
        <div className="form-actions">
          {saved && <span className="muted small">تم الحفظ</span>}
          <button className="btn btn-primary" disabled={save.busy || !valid}>
            {save.busy ? "جارٍ الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
      {s?.hasToken && s.active && <WhatsAppLink organizationId={organizationId} />}
      {s?.hasToken && (
        <div className="section form" style={{ borderTop: "1px solid var(--border-default)" }}>
          <h3 className="small">رسالة اختبار</h3>
          <ErrorNote error={test.error} />
          <div className="form-row" style={{ alignItems: "end" }}>
            <Field id="wa-test" label="أرسل إلى رقم">
              <PhoneField id="wa-test" value={testPhone} onChange={setTestPhone} />
            </Field>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={test.busy || testPhone.replace(/\D/g, "").length < 9}
              onClick={sendTest}
            >
              {test.busy ? "جارٍ الإرسال…" : "إرسال رسالة اختبار"}
            </button>
          </div>
          {result && (
            <p className={result.ok ? "notice small" : "alert"}>
              {result.ok ? "وصلت الرسالة إلى خدمة الواتساب بنجاح." : `فشل الإرسال: ${result.error}`}
            </p>
          )}
          {s.lastTestedAt && (
            <p className="muted small">آخر اختبار: {new Date(s.lastTestedAt).toLocaleString("ar-SA")}</p>
          )}
        </div>
      )}
    </section>
  );
}

const stateLabel: Record<WhatsAppLinkState, string> = {
  connected: "متصل",
  qr_ready: "بانتظار مسح الرمز",
  initializing: "جارٍ التجهيز",
  reconnecting: "يعيد الاتصال",
  disconnected: "غير متصل",
  not_initialized: "غير مربوط",
  unavailable: "تعذر الوصول للخدمة",
};

/** Linking the sending number by QR from the portal (the gateway's instance routes), like Deem's settings. */
function WhatsAppLink({ organizationId }: { organizationId: string }) {
  const qc = useQueryClient();
  const key = ["whatsapp-connection", organizationId];
  const action = useSubmit();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const conn = useQuery({
    queryKey: key,
    queryFn: () =>
      api<WhatsAppConnectionResponse>(`/v1/whatsapp-settings/connection?organizationId=${organizationId}`),
    // While a QR is waiting or the gateway is getting ready, follow it until the number is linked.
    refetchInterval: (q) =>
      ["qr_ready", "initializing", "reconnecting"].includes(q.state.data?.state ?? "") ? 3000 : false,
  });

  function run(kind: "connect" | "relink" | "logout") {
    setConfirmLogout(false);
    void action.run(async () => {
      const r = await send<WhatsAppConnectionResponse>("POST", "/v1/whatsapp-settings/connection", {
        organizationId,
        action: kind,
      });
      qc.setQueryData(key, r);
      if (r.state !== "connected") await qc.invalidateQueries({ queryKey: key });
    }, errorText);
  }

  const c = conn.data;
  const busy = action.busy || conn.isFetching;
  return (
    <div className="section form" style={{ borderTop: "1px solid var(--border-default)" }}>
      <div className="card-h" style={{ padding: 0 }}>
        <h3 className="small">ربط رقم الواتساب</h3>
        {c && (
          <Badge
            tone={c.state === "connected" ? "success" : c.state === "unavailable" ? "danger" : "neutral"}
          >
            {stateLabel[c.state]}
          </Badge>
        )}
      </div>
      <ErrorNote error={action.error ?? (conn.isError ? errorText(conn.error) : null)} />
      {c?.error && <p className="alert">{c.error}</p>}
      {c?.state === "connected" ? (
        <>
          <p className="small">
            الرموز تُرسل من الرقم{" "}
            <b className="ltr">{c.phone ? `+${c.phone.replace(/\D/g, "")}` : "المربوط"}</b>.
          </p>
          <div className="form-actions">
            {confirmLogout ? (
              <>
                <span className="muted small">
                  بعد قطع الاتصال لن تصل رموز الدخول حتى تربط رقمًا من جديد.
                </span>
                <button
                  type="button"
                  className="btn btn-danger"
                  disabled={busy}
                  onClick={() => run("logout")}
                >
                  تأكيد قطع الاتصال
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmLogout(false)}>
                  إلغاء
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmLogout(true)}>
                قطع الاتصال
              </button>
            )}
          </div>
        </>
      ) : c?.qrCode ? (
        <>
          <p className="small">
            افتح واتساب في جوال الجمعية ← الإعدادات ← الأجهزة المرتبطة ← ربط جهاز، ثم امسح الرمز. يتحدث الرمز
            تلقائيًا.
          </p>
          <img
            src={c.qrCode}
            alt="رمز QR لربط الواتساب"
            width={240}
            height={240}
            style={{ alignSelf: "center", background: "#fff", padding: 8, borderRadius: 8 }}
          />
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => run("relink")}>
              رمز جديد
            </button>
          </div>
        </>
      ) : (
        c &&
        c.state !== "unavailable" && (
          <>
            <p className="muted small">
              {c.state === "initializing" || c.state === "reconnecting"
                ? "لحظات ويظهر رمز الربط…"
                : "اربط رقم واتساب الجمعية الذي تُرسل منه الرموز."}
            </p>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => run(c.state === "disconnected" ? "relink" : "connect")}
              >
                {action.busy ? "لحظة…" : "ربط رقم واتساب"}
              </button>
            </div>
          </>
        )
      )}
    </div>
  );
}
